/**
 * i18n (EN / FR) for the whole dashboard.
 *
 * Every user-facing string in the app resolves through these dictionaries.
 * French is the operators' working language (Ciments du Maroc finance);
 * English is the fallback. The choice persists in localStorage and is
 * switchable from the avatar menu. <html lang> and the document title stay
 * in sync with the active language.
 */
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import { LS_LANG_KEY } from "./lib/config";

const resources = {
  en: {
    translation: {
      customer: {
        select: "Customer",
        choose: "Choose a customer",
        current: "Current customer — every page and action works on it",
        none: "No customer",
        noneHint: "No customer is assigned to your account. Ask an administrator to assign one.",
        chooseHint: "Choose a customer in the top bar to see its runs, files and figures.",
        editingHint: "You are the editor of this customer; everyone else connected to it is viewing.",
        viewingHint: "{{name}} is editing this customer. You can look but not change anything; you will be able to edit as soon as they move on.",
        nowEditing: "You can now edit this customer.",
        readOnlyAction: "{{name}} is editing this customer; you are in viewing mode.",
      },
      customers: {
        title: "Customers",
        subtitle: "The companies the platform closes the books for. Deactivating one hides it from every selector; nothing is deleted.",
        create: "New customer",
        createTitle: "New customer",
        editTitle: "Edit the customer",
        empty: "No customer yet.",
        colName: "Customer",
        colIdentity: "Identity",
        colContact: "Contact",
        colCustomers: "Customers",
        name: "Name",
        code: "Code",
        codeHelp: "Short, unique: letters, digits, - or _",
        sapCompanyCode: "SAP company code",
        reportingUnit: "Tagetik reporting unit",
        currency: "Currency",
        phone: "Phone",
        email: "E-mail",
        address: "Address",
        notes: "Notes",
        save: "Save",
        saved: "Customer saved.",
        edit: "Edit",
        activate: "Activate",
        deactivate: "Deactivate",
        activated: "Customer activated.",
        deactivated: "Customer deactivated — hidden from every selector.",
        status: { active: "Active", inactive: "Inactive" },
        assign: "Customers of this account",
        assignTitle: "Customers of {{name}}",
        assignBody: "The account works on the customers ticked here, and on nothing else.",
        assignSysadmin: "A system administrator works on every customer.",
        assignAll: "All customers",
        assignNone: "None assigned",
        assigned: "Customers assigned.",
      },
      api: {
        requestFailed: "Request failed ({{status}})",
        downloadFailed: "Download failed ({{status}})",
      },
      app: {
        title: "Automated Enterprise Excel Engine",
        tagline: "Deterministic calculators; the agent orchestrates, it does not compute.",
        build: "build",
        newVersion: "A new version of the dashboard is deployed —",
        refresh: "refresh",
        searchPlaceholder: "Search tools, runs, files…",
        searchLabel: "Search tools, runs and files",
        language: "Language",
        english: "English",
        french: "French",
        operator: "Operator",
        accountMenu: "Account and language menu",
        theme: "Theme",
        themeLight: "Light",
        themeDark: "Dark",
        themeSystem: "System",
      },
      common: {
        retry: "Retry",
        aiDisabled: "This service is switched off for now.",
        close: "Close",
        cancel: "Cancel",
        home: "Back to the dashboard",
        copyId: "Copy identifier",
        copied: "Identifier copied.",
        copyFailed: "The identifier could not be copied.",
        textCopied: "Copied.",
        textCopyFailed: "The text could not be copied.",
      },
      errors: {
        network: "Cannot reach the server",
        networkHint: "Check your connection, then retry.",
        loadFailed: "Something went wrong",
        unknown: "An unexpected error occurred.",
      },
      notFound: {
        title: "Page not found",
        message: "This page does not exist. Use the menu on the left, or go back to the dashboard.",
      },
      nav: {
        dashboard: "Dashboard",
        wizard: "Run pipeline",
        singleTool: "Use a single tool",
        runs: "Runs",
        approvals: "Approvals",
        audit: "Audit assistance",
        users: "Users",
        customers: "Customers",
        collapse: "Collapse menu",
        openMenu: "Open the menu",
      },
      auth: {
        anonymousName: "Operator",
        unknownUser: "unknown",
        ssoNotConfigured:
          "Single sign-on is enabled but not configured on the server. OIDC_ISSUER, OIDC_CLIENT_ID and OIDC_SCOPES must be set from the Entra app registrations.",
        brandTagline: "Deterministic calculators. Auditable pipelines. Sign in with your platform account to continue.",
        loginTitle: "Log in",
        registerTitle: "Request access",
        noAccount: "No account yet?",
        haveAccount: "Already have an account?",
        requestAccess: "Request access",
        email: "Email address",
        password: "Password",
        confirmPassword: "Confirm password",
        fullName: "Full name",
        currentPassword: "Current password",
        newPassword: "New password",
        signIn: "Sign in",
        signingIn: "Signing in…",
        signOut: "Sign out",
        submitRequest: "Submit request",
        submitting: "Submitting…",
        backToLogin: "Back to sign in",
        approvalNote: "Access requests are approved by a system administrator.",
        ssoSubtitle: "Sign in with your organisation account.",
        ssoButton: "Sign in with your organisation account",
        ssoNote:
          "You will be redirected to your organisation's sign-in page. Your password is never sent to this application.",
        approvalInfo: "New accounts require approval by a system administrator. You can sign in once your request is approved.",
        requestSentTitle: "Request submitted",
        requestSentBody: "Your request was submitted. A system administrator must approve it before you can sign in.",
        passwordTooShort: "At least {{min}} characters.",
        passwordMismatch: "The passwords do not match.",
        genericError: "Something went wrong. Please try again.",
        accountMenu: "Account menu",
        manageUsers: "User administration",
        changePassword: "Change password",
        mustChangePassword: "You signed in with a temporary password. Set a new password to continue.",
        passwordChanged: "Your password was changed.",
        noSessionMode: "Authentication is disabled",
        role: { operator: "Operator", approver: "Approver", sysadmin: "Administrator" },
      },
      admin: {
        title: "User administration",
        subtitle: "Every account on the platform. Approve access requests, reset passwords, and manage access.",
        ssoSubtitle: "Under single sign-on, identities and roles are held by Microsoft Entra ID, not by this platform.",
        localAccountsTitle: "Local accounts (inactive under single sign-on)",
        pendingBadge_one: "{{count}} request awaiting approval",
        pendingBadge_other: "{{count}} requests awaiting approval",
        empty: "No accounts yet.",
        colName: "User",
        colRole: "Role",
        colStatus: "Status",
        colLastLogin: "Last sign-in",
        colActions: "Actions",
        never: "Never",
        locked: "Locked",
        approve: "Approve",
        reject: "Reject",
        resetPassword: "Reset password",
        activate: "Activate",
        deactivate: "Deactivate",
        delete: "Delete",
        copy: "Copy",
        approved: "Account approved.",
        rejected: "Request rejected.",
        passwordReset: "Password reset — share the temporary password.",
        activated: "Account activated.",
        deactivated: "Account deactivated.",
        deleted: "Account deleted.",
        actionFailed: "The action could not be completed.",
        status: { pending: "Pending", active: "Active", inactive: "Inactive", deleted: "Deleted" },
        deleteTitle: "Delete this account?",
        deleteBody: "This deactivates {{name}} ({{email}}) and revokes their access. Their past actions stay attributed. Type the email to confirm.",
        deleteConfirmLabel: "Type the email to confirm",
        tempPasswordTitle: "Temporary password",
        tempPasswordBody: "Share this one-time password with {{email}}. They must change it at next sign-in.",
        tempPasswordWarning: "This password is shown only once and cannot be retrieved again.",
        newUser: "New user",
        newUserTitle: "Create an account",
        newUserBody: "The account is active at once. You will get a temporary password to hand over; the person changes it at first sign-in.",
        fullName: "Full name",
        email: "Email",
        role: "Role",
        sysadminCustomers: "An administrator works on every customer.",
        create: "Create",
        created: "Account created — share the temporary password.",
      },
      approvals: {
        bell: "Pending approvals",
        title: "Pending approvals",
        none: "No approval is waiting for you.",
        loadFailed: "Pending approvals could not be loaded.",
        subtitle: "Checkpoints waiting for a human decision before their run continues.",
        tabPending: "Pending",
        tabHistory: "History",
        historyHint: "Decisions are recorded run by run — open a run's detail page to see what was approved, rejected or replaced.",
        review: "Review and decide",
        openRun: "Open the run",
        emptyInbox: "Nothing is waiting for your decision right now.",
      },
      runs: {
        title: "Runs",
        subtitle: "Every run of the platform — open a row for the full detail.",
        filterCategory: "State",
        searchPlaceholder: "Pipeline or run id…",
        searchLabel: "Filter runs by pipeline name or run id",
        cat: {
          all: "All states",
          running: "Running",
          waiting: "Awaiting approval",
          success: "Completed",
          warning: "With controls",
          failure: "Failed",
        },
        state: "State",
        pipeline: "Pipeline",
        created: "Started",
        job: "Run id",
        details: "Details",
        open: "Open run {{id}}",
        empty: "No run yet — launch a tool or the full pipeline.",
        noMatch: "No run matches the current filters.",
        resetFilters: "Clear the filters",
        exportCsv: "Export CSV",
        densityCompact: "Compact rows",
        densityComfortable: "Comfortable rows",
        densityLabel: "Row density",
        forkOf: "fork of {{id}}",
        filterYear: "Year",
        filterMonth: "Month",
        filterDay: "Day",
        anyYear: "Any year",
        anyMonth: "Any month",
        anyDay: "Any day",
        dateFilterLabel: "Filter runs by start date",
      },
      runDetail: {
        title: "Run detail",
        back: "All runs",
        created: "Started",
        finished: "Finished",
        launchedBy: "Launched by",
        forkedFrom: "Forked from run",
        forks_one: "{{count}} fork of this run:",
        forks_other: "{{count}} forks of this run:",
        steps: "Steps",
        attempt: "attempt {{count}}",
        rows: "{{in}} → {{out}} rows",
        errorSummary_one: "{{count}} control/step needs your attention",
        errorSummary_other: "{{count}} controls/steps need your attention",
        errorSummaryHint: "Each link scrolls to the step concerned.",
        showError: "Show the error detail",
        hideError: "Hide the error detail",
        askAudit: "Ask a question about this run",
        viewRun: "View the run",
        awaiting: "This run is paused at an approval checkpoint.",
      },
      toasts: {
        jobCompleted: "Run completed.",
        jobCompletedControls: "Run completed — with control findings to review.",
        jobFailed: "Run failed.",
        approvalDecided: "Decision recorded.",
      },
      dashboard: {
        title: "Activity overview",
        subtitle: "Usage of the platform over the last {{days}} days",
        totalRuns: "Total runs",
        uploads: "Files uploaded",
        outputs: "Files produced",
        completed: "Completed runs",
        activity: "Daily activity",
        runs: "Runs",
        uploadsSeries: "Uploads",
        toolUsage: "Tool usage",
        recentJobs: "Recent runs",
        quickRunning: "Running",
        quickWaiting: "Awaiting approval",
        quickWarning: "Completed with controls",
        quickFailure: "Failed",
        job: "Run",
        pipeline: "Pipeline",
        state: "State",
        date: "Date",
        empty: "No activity yet — upload a file and run a tool.",
        activityChart: "Bar chart of daily runs and uploads",
        toolUsageChart: "Bar chart of run counts per tool",
        review: "Reviews",
        reviewPending: "Awaiting a decision now",
        reviewMedian: "Median time to decide",
        reviewMonth: "Decisions this month",
        approved: "approved",
        rejected: "rejected",
        replaced: "replaced",
        health: "Tool health",
        healthTool: "Tool",
        healthRuns: "Runs",
        healthSuccess: "Succeeded",
        healthControls: "With failed controls",
        healthDuration: "Median duration",
      },
      monthly: {
        title: "Closing, month by month",
        subtitleAll: "Cumulative of all {{count}} Summary accounts, by activity — the last successful run of each month.",
        subtitleAccount: "Account {{account}}, by activity — the last successful run of each month.",
        year: "Closing year",
        activity: "Activity",
        all: "All",
        total: "Total",
        account: "Account (all accounts when empty)",
        empty: "No figure yet for this selection — run the exceptional-items mapping with a closing period.",
        chartLabel: "Bar chart of the selected account per month — {{series}}",
        calendar: "Closing calendar {{year}} — state of the latest run of each month. Click a month to see the accounts that changed during it.",
        noRun: "No run for this month",
        uploads_one: "{{count}} upload",
        uploads_other: "{{count}} uploads",
        noUpload: "No upload for this month",
        clickHint: "Click a month to see the accounts changed",
        chartClickHint: "Click a bar for the month's uploads and account changes",
        changesTitle: "{{month}} — accounts changed during the month",
        changesSubtitle: "{{uploads}} · {{compared}} with figures · {{changed}} account lines changed",
        changesLoading: "Comparing the uploads of the month…",
        timeline: "Uploads of the month, in order",
        uploadN: "Upload {{n}}",
        by: "by {{user}}",
        noFile: "no entry file",
        notHarvested: "no figures",
        openRun: "Open the run",
        close: "Close",
        changedOnly: "Changed only",
        allAccounts: "All accounts",
        searchAccount: "Account or label…",
        colAccount: "Account",
        colLabel: "Label",
        colActivity: "Activity",
        colDelta: "Δ last − first",
        absent: "absent",
        noUploads: "No upload was launched for this month.",
        noFigures: "No upload of this month produced figures yet — the accounts cannot be compared.",
        needsNextTool: "Upload {{n}} ran {{tool}}, which produces no figures by itself: they appear once {{next}} has run on its output.",
        runNextTool: "Run {{tool}} on it",
        figuresSource: "Figures from {{tool}}",
        figuresAtLevel: "These figures come from {{tool}}. Run {{next}} on the latest upload's output for the mapped figures; they will take over this month.",
        launching: "Launching…",
        nextToolStarted: "{{tool}} started on the produced file — its figures appear here once it completes.",
        oneUpload: "A single upload produced figures this month — nothing to compare yet; its figures are shown.",
        noChange: "No account changed between the uploads of this month.",
        noMatch: "No account matches the search.",
      },
      wizard: {
        stepCount_one: "{{count}} step, reviewed before the next",
        stepCount_other: "{{count}} steps, each reviewed before the next",
        title: "Full pipeline",
        subtitle: "The pipeline runs every declared step in order, pausing at each approval checkpoint.",
        stepUpload: "Upload the entry file",
        stepIdentity: "Run identity",
        stepRun: "Launch and follow",
        identityHelp: "The pipeline's final step needs these values — they stamp every row it produces.",
        identityUnavailable: "The identity fields could not be loaded, so the pipeline cannot be launched safely.",
        pickDag: "Choose a pipeline",
        changeDag: "Choose another pipeline",
        launch: "Start the pipeline",
        launching: "Starting…",
        next: "Next",
        back: "Back",
        follow: "Pipeline progress",
        awaiting: "This step is waiting for your approval.",
        review: "Review and decide",
        files: "Produced files",
        newRun: "Start another run",
        noDag: "No pipeline is declared yet.",
        uploadFirst: "Upload a file to continue.",
        noFileNeeded: "No file is needed for the selected data source.",
        entryParams: "Data source",
        forkFrom: "Starting from an existing file: {{name}}. The run will be recorded as a fork of the run that produced it. Drop another file to replace it.",
        fillIdentity: "Every identity field is required for a pipeline run.",
      },
      params: {
        unknownControl: "Unknown field type: {{control}}",
      },
      tools: {
        title: "Single tool",
        subtitle: "Pick a tool, upload its input, run it, download the result.",
        pick: "1 · Choose a tool",
        upload: "2 · Data source",
        run: "3 · Run",
        progress: "4 · Progress",
        files: "5 · Files",
        requiredColumns: "Expected columns",
        checkingFile: "Checking the uploaded file against this tool…",
        fileOk: "The uploaded file matches this tool's input.",
        fileMismatch: "This file does not fit this tool — running it would fail:",
        fileMismatchHint: "Pick the tool that matches the file, or upload the right file.",
        runButton: "Run {{name}}",
        running: "Running…",
        missing: "Missing required field(s): {{fields}}",
        overrideReason: "Override reason (required when overriding)",
        advanced: "Advanced parameters",
        uploadPrompt: "Drop an .xlsx file here or click to browse",
        uploading: "Uploading…",
        hitl: "Approval",
        version: "version",
        empty: "No tool is declared yet.",
        multiSlot: "This tool declares several input files — this version of the dashboard supports only the first one.",
        noFileNeeded: "No file is needed for the selected data source.",
      },
      audit: {
        title: "Audit assistance",
        subtitle: "Ask about runs, figures, rules — or attach a workbook and ask about it.",
        placeholder: "e.g. What is the total of account 5311100020 by year?",
        send: "Send",
        thinking: "Analysing…",
        attach: "Attach a workbook",
        attached: "Attached: {{name}}",
        removeAttachment: "Remove attachment",
        sqlUsed: "SQL used",
        sources: "Sources",
        intent: {
          qualitative: "rules",
          quantitative: "data",
          hybrid: "rules + data",
          file: "attached file",
        },
        gathered: {
          run: "run in scope",
          attachment: "file read",
          documents: "rule documents",
          query: "database query",
        },
        readingNotes: "What the assistant read in the attached file",
        empty: "Ask a first question — the assistant reads the run database and your rule documents.",
        context: "Context: run {{id}}",
        contextRemove: "Remove the run context",
        clear: "Clear the conversation",
        copyAnswer: "Copy the answer",
        copySql: "Copy the SQL",
      },
      auditActions: {
        heading: "Suggested actions (nothing runs without your confirmation)",
        fixParams: "Fix the run parameters and relaunch",
        fixFile: "Correct the file and relaunch",
        reviewGuardrails: "Review the guardrail failures",
        rerunSameInput: "Relaunch with the same input file",
        openApprovals: "Open pending approvals",
        reviewControls: "Review the failed controls",
        retryJob: "Relaunch this run",
        relaunchFromTool: "Relaunch from the tool page",
        confirmTitle: "Confirm the relaunch",
        confirmBody:
          "A new run of {{tool}} will be launched on file {{file}}…. Nothing else is modified. Confirm?",
        confirmRetryBody:
          "Run {{id}}… will be relaunched as a NEW run, replaying its recorded input file and parameters. The original run is kept unchanged. Confirm?",
        confirm: "Confirm and relaunch",
        launching: "Launching…",
        rerunStarted: "New run launched.",
      },
      files: {
        download: "Download",
        rows_one: "{{count}} row",
        rows_other: "{{count}} rows",
        noRows: "workbook",
        produced: "Produced files",
        lineage: "Lineage",
        lineageOriginal: "Original upload — no parent artifact.",
        fork: "Run a tool from this file (fork)",
        fromRun: "from run {{id}}",
        inRun: "run {{id}}",
        usedBy: "Used by:",
        relationDerived: "produced from",
        relationReplaced: "replaced by upload",
        relationRevised: "revised with the assistant",
        unmatchedShort_one: "{{count}} unmatched row",
        unmatchedShort_other: "{{count}} unmatched rows",
      },
      upload: {
        constraint: ".xlsx · up to {{mb}} MB",
        badExtension: "{{name}} — only .xlsx / .xlsm files are accepted.",
        tooLarge: "{{name}}: {{size}} MB exceeds the {{max}} MB limit.",
        nameHint: "ending with _{{code}}.xlsx",
        badFormat: "{{name}} must end with _{{code}}.xlsx, the code of the customer you are working on.",
        customerMismatch: "{{name}} is named for company code \"{{found}}\" but you are working on {{customer}} ({{code}}). Rename the file or change the customer.",
      },
      progress: {
        state: "State",
        step: "Step",
        rowsOut: "Rows",
        error: "Error detail",
      },
      approval: {
        title: "Approval — {{step}}",
        approve: "Approve",
        reject: "Reject",
        replace: "Replace file and approve",
        note: "Note",
        decided: "Decision recorded.",
        chooseFile: "Choose the corrected workbook to upload.",
        chosenFile: "Selected: {{name}}",
        output: "output",
        askAgent: "Ask the agent",
        askAgentAgain: "Ask the agent again",
        agentBusy: "The agent is reviewing the document…",
        agentHeading: "Agent review",
        agentQuestion:
          "Review the document produced by step {{step}} before its approval: tool warnings, failed controls, unmatched rows, anomalies. Say what blocks approval, what is only to be checked, and what to correct before approving.",
        unmatchedWarning_one: "{{count}} row was not covered by an explicit rule and fell back to a default value. Check it before approving.",
        unmatchedWarning_other: "{{count}} rows were not covered by an explicit rule and fell back to a default value. Check them before approving.",
      },
      states: {
        COMPLETED: "Completed",
        RUNNING: "Running",
        FAILED: "Failed",
        VALIDATION_FAILED: "Validation failed",
        AWAITING_HUMAN_APPROVAL: "Awaiting approval",
        INITIALIZED: "Initialized",
        SUCCEEDED: "Succeeded",
        SUCCESS: "Succeeded",
        PENDING: "Pending",
        QUEUED: "Queued",
        SUBMITTED: "Submitted",
        CLAIMED: "Claimed",
        STARTED: "Started",
        PAUSED: "Paused",
        AWAITING_APPROVAL: "Awaiting approval",
        REJECTED: "Rejected",
        SKIPPED: "Skipped",
        CANCELLED: "Cancelled",
        COMPLETED_WITH_CONTROLS: "Completed with controls",
        SUCCESS_WITH_CONTROLS: "Completed with controls",
      },
      search: {
        tools: "Tools",
        jobs: "Runs",
        files: "Files",
        noResults: "No results for “{{q}}”",
      },
      controls: {
        title: "Reconciliation controls",
        inputFile: "Processed input file",
        comparedTo: "Compared to the previous run: {{before}} failed control(s) before, {{after}} now.",
        previously: "Previous run",
        delta: {
          cleared: "cleared",
          detailChanged: "changed — your fix was processed",
          unchanged: "unchanged",
          regressed: "regressed",
        },
        ok: "OK",
        failed: "FAILED",
        downloadPdf: "Download report (PDF)",
        downloadingPdf: "Preparing…",
        downloadPdfHint:
          "Every control, in plain language — including what the legacy workbook never reported.",
        downloadPdfFailed: "The report could not be produced.",
        flaggedLines_one: "{{count}} line to review",
        flaggedLines_other: "{{count}} lines to review",
        fixTitle: "Fix and re-process",
        fixHint: "Describe a correction to the input file — the assistant turns it into precise edits you review before anything is applied.",
        fixHintFailed: "Some controls failed. Describe the correction in your own words — the assistant turns it into precise edits on the input file, which you review before anything is applied.",
        instructionPlaceholder: "e.g. Remove the M540000102 and M540000000 rows from the CEM sheet — they are subtotals outside the referential.",
        propose: "Propose a fix",
        proposing: "Analysing…",
        emptyPlan: "The assistant could not translate this instruction into file edits.",
        opAction: "Action",
        opTarget: "Target",
        opReason: "Why",
        op: {
          set_cell: "Set cell",
          delete_rows: "Delete row(s)",
          replace_value: "Replace value",
        },
        apply: "Apply and create the corrected file",
        applying: "Applying…",
        appliedMessage: "{{name}} created — {{count}} change(s). Review it, then re-process.",
        rerun: "Re-process",
        rerunning: "Starting…",
      },
      validation: {
        statuses: {
          ok: "OK",
          moved: "Moved",
          renamed: "Renamed",
          missing: "Missing",
          extra: "Unexpected",
        },
        title: "Validation report",
        errors_one: "{{count}} error",
        errors_other: "{{count}} errors",
        row: "Row",
        column: "Column",
        code: "Code",
        value: "Value",
        detail: "Detail",
        driftTitle: "Template drift detected",
        driftBody: "The uploaded workbook does not match the declared template. Nothing was calculated.",
        expected: "Expected",
        index: "Index",
        found: "Found",
        status: "Status",
        columnStat: "{{column}}: {{invalid}} invalid, {{empty}} empty",
      },
    },
  },
  fr: {
    translation: {
      customer: {
        select: "Client",
        choose: "Choisir un client",
        current: "Client courant — chaque page et chaque action porte sur lui",
        none: "Aucun client",
        noneHint: "Aucun client n'est affecté à votre compte. Demandez à un administrateur de vous en affecter un.",
        chooseHint: "Choisissez un client dans la barre du haut pour voir ses traitements, ses fichiers et ses chiffres.",
        editingHint: "Vous êtes l'éditeur de ce client ; les autres personnes connectées sont en consultation.",
        viewingHint: "{{name}} édite ce client. Vous pouvez consulter mais rien modifier ; vous pourrez éditer dès qu'il passera à autre chose.",
        nowEditing: "Vous pouvez maintenant éditer ce client.",
        readOnlyAction: "{{name}} édite ce client ; vous êtes en mode consultation.",
      },
      customers: {
        title: "Clients",
        subtitle: "Les sociétés dont la plateforme fait la clôture. Désactiver un client le retire de tous les sélecteurs ; rien n'est supprimé.",
        create: "Nouveau client",
        createTitle: "Nouveau client",
        editTitle: "Modifier le client",
        empty: "Aucun client pour l'instant.",
        colName: "Client",
        colIdentity: "Identité",
        colContact: "Contact",
        colCustomers: "Clients",
        name: "Nom",
        code: "Code",
        codeHelp: "Court et unique : lettres, chiffres, - ou _",
        sapCompanyCode: "Code société SAP",
        reportingUnit: "Reporting unit Tagetik",
        currency: "Devise",
        phone: "Téléphone",
        email: "E-mail",
        address: "Adresse",
        notes: "Notes",
        save: "Enregistrer",
        saved: "Client enregistré.",
        edit: "Modifier",
        activate: "Activer",
        deactivate: "Désactiver",
        activated: "Client activé.",
        deactivated: "Client désactivé — retiré de tous les sélecteurs.",
        status: { active: "Actif", inactive: "Inactif" },
        assign: "Clients de ce compte",
        assignTitle: "Clients de {{name}}",
        assignBody: "Le compte travaille sur les clients cochés ici, et sur rien d'autre.",
        assignSysadmin: "Un administrateur système travaille sur tous les clients.",
        assignAll: "Tous les clients",
        assignNone: "Aucun client affecté",
        assigned: "Clients affectés.",
      },
      api: {
        requestFailed: "Échec de la requête ({{status}})",
        downloadFailed: "Échec du téléchargement ({{status}})",
      },
      app: {
        title: "Moteur Excel Automatisé",
        tagline: "Calculateurs déterministes ; l'agent orchestre, il ne calcule pas.",
        build: "version",
        newVersion: "Une nouvelle version du tableau de bord est déployée —",
        refresh: "actualiser",
        searchPlaceholder: "Rechercher outils, traitements, fichiers…",
        searchLabel: "Rechercher outils, traitements et fichiers",
        language: "Langue",
        english: "Anglais",
        french: "Français",
        operator: "Opérateur",
        accountMenu: "Menu compte et langue",
        theme: "Thème",
        themeLight: "Clair",
        themeDark: "Sombre",
        themeSystem: "Système",
      },
      common: {
        retry: "Réessayer",
        aiDisabled: "Service désactivé pour le moment.",
        close: "Fermer",
        cancel: "Annuler",
        home: "Retour au tableau de bord",
        copyId: "Copier l'identifiant",
        copied: "Identifiant copié.",
        copyFailed: "L'identifiant n'a pas pu être copié.",
        textCopied: "Copié.",
        textCopyFailed: "Le texte n'a pas pu être copié.",
      },
      errors: {
        network: "Le serveur est injoignable",
        networkHint: "Vérifiez votre connexion, puis réessayez.",
        loadFailed: "Une erreur est survenue",
        unknown: "Une erreur inattendue s'est produite.",
      },
      notFound: {
        title: "Page introuvable",
        message: "Cette page n'existe pas. Utilisez le menu de gauche ou revenez au tableau de bord.",
      },
      nav: {
        dashboard: "Accueil",
        wizard: "Lancer le pipeline",
        singleTool: "Utiliser un outil",
        runs: "Exécutions",
        approvals: "Approbations",
        audit: "Piste d'audit",
        users: "Utilisateurs",
        customers: "Clients",
        collapse: "Réduire le menu",
        openMenu: "Ouvrir le menu",
      },
      auth: {
        anonymousName: "Opérateur",
        unknownUser: "inconnu",
        ssoNotConfigured:
          "L'authentification unique est activée mais non configurée côté serveur. OIDC_ISSUER, OIDC_CLIENT_ID et OIDC_SCOPES doivent être renseignés depuis les enregistrements d'application Entra.",
        brandTagline: "Calculateurs déterministes. Traitements auditables. Connectez-vous avec votre compte pour continuer.",
        loginTitle: "Connexion",
        registerTitle: "Demander un accès",
        noAccount: "Pas encore de compte ?",
        haveAccount: "Vous avez déjà un compte ?",
        requestAccess: "Demander un accès",
        email: "Adresse e-mail",
        password: "Mot de passe",
        confirmPassword: "Confirmer le mot de passe",
        fullName: "Nom complet",
        currentPassword: "Mot de passe actuel",
        newPassword: "Nouveau mot de passe",
        signIn: "Se connecter",
        signingIn: "Connexion…",
        signOut: "Se déconnecter",
        submitRequest: "Envoyer la demande",
        submitting: "Envoi…",
        backToLogin: "Retour à la connexion",
        approvalNote: "Les demandes d'accès sont approuvées par un administrateur système.",
        ssoSubtitle: "Connectez-vous avec votre compte d'entreprise.",
        ssoButton: "Se connecter avec le compte d'entreprise",
        ssoNote:
          "Vous serez redirigé vers la page de connexion de votre organisation. Votre mot de passe n'est jamais transmis à cette application.",
        approvalInfo: "Les nouveaux comptes doivent être approuvés par un administrateur système. Vous pourrez vous connecter une fois votre demande approuvée.",
        requestSentTitle: "Demande envoyée",
        requestSentBody: "Votre demande a été envoyée. Un administrateur système doit l'approuver avant que vous puissiez vous connecter.",
        passwordTooShort: "Au moins {{min}} caractères.",
        passwordMismatch: "Les mots de passe ne correspondent pas.",
        genericError: "Une erreur est survenue. Veuillez réessayer.",
        accountMenu: "Menu du compte",
        manageUsers: "Administration des utilisateurs",
        changePassword: "Changer le mot de passe",
        mustChangePassword: "Vous vous êtes connecté avec un mot de passe temporaire. Définissez un nouveau mot de passe pour continuer.",
        passwordChanged: "Votre mot de passe a été changé.",
        noSessionMode: "L'authentification est désactivée",
        role: { operator: "Opérateur", approver: "Approbateur", sysadmin: "Administrateur" },
      },
      admin: {
        title: "Administration des utilisateurs",
        subtitle: "Tous les comptes de la plateforme. Approuvez les demandes d'accès, réinitialisez les mots de passe et gérez les accès.",
        localAccountsTitle: "Comptes locaux (inactifs sous authentification unique)",
        pendingBadge_one: "{{count}} demande en attente d'approbation",
        pendingBadge_many: "{{count}} demandes en attente d'approbation",
        pendingBadge_other: "{{count}} demandes en attente d'approbation",
        empty: "Aucun compte pour l'instant.",
        colName: "Utilisateur",
        colRole: "Rôle",
        colStatus: "Statut",
        colLastLogin: "Dernière connexion",
        colActions: "Actions",
        never: "Jamais",
        locked: "Verrouillé",
        approve: "Approuver",
        reject: "Rejeter",
        resetPassword: "Réinitialiser le mot de passe",
        activate: "Activer",
        deactivate: "Désactiver",
        delete: "Supprimer",
        copy: "Copier",
        approved: "Compte approuvé.",
        rejected: "Demande rejetée.",
        passwordReset: "Mot de passe réinitialisé — communiquez le mot de passe temporaire.",
        activated: "Compte activé.",
        deactivated: "Compte désactivé.",
        deleted: "Compte supprimé.",
        actionFailed: "L'action n'a pas pu être effectuée.",
        status: { pending: "En attente", active: "Actif", inactive: "Inactif", deleted: "Supprimé" },
        deleteTitle: "Supprimer ce compte ?",
        deleteBody: "Cette action désactive {{name}} ({{email}}) et révoque son accès. Ses actions passées restent attribuées. Saisissez l'e-mail pour confirmer.",
        deleteConfirmLabel: "Saisissez l'e-mail pour confirmer",
        tempPasswordTitle: "Mot de passe temporaire",
        tempPasswordBody: "Communiquez ce mot de passe à usage unique à {{email}}. Il devra le changer à la prochaine connexion.",
        tempPasswordWarning: "Ce mot de passe n'est affiché qu'une seule fois et ne peut pas être récupéré.",
        newUser: "Nouvel utilisateur",
        newUserTitle: "Créer un compte",
        newUserBody: "Le compte est actif immédiatement. Vous obtiendrez un mot de passe temporaire à communiquer ; la personne le changera à sa première connexion.",
        fullName: "Nom complet",
        email: "E-mail",
        role: "Rôle",
        sysadminCustomers: "Un administrateur travaille sur tous les clients.",
        create: "Créer",
        created: "Compte créé — communiquez le mot de passe temporaire.",
      },
      approvals: {
        bell: "Approbations en attente",
        title: "Approbations en attente",
        none: "Aucune approbation ne vous attend.",
        loadFailed: "Les approbations en attente n'ont pas pu être chargées.",
        subtitle: "Points de contrôle en attente d'une décision humaine avant la poursuite du traitement.",
        tabPending: "En attente",
        tabHistory: "Historique",
        historyHint: "Les décisions sont enregistrées traitement par traitement — ouvrez la page de détail d'une exécution pour voir ce qui a été approuvé, rejeté ou remplacé.",
        review: "Examiner et décider",
        openRun: "Ouvrir l'exécution",
        emptyInbox: "Rien n'attend votre décision pour l'instant.",
      },
      runs: {
        title: "Exécutions",
        subtitle: "Toutes les exécutions de la plateforme — ouvrez une ligne pour le détail complet.",
        filterCategory: "État",
        searchPlaceholder: "Pipeline ou identifiant…",
        searchLabel: "Filtrer les exécutions par pipeline ou identifiant",
        cat: {
          all: "Tous les états",
          running: "En cours",
          waiting: "En attente d'approbation",
          success: "Terminées",
          warning: "Avec contrôles",
          failure: "En échec",
        },
        state: "État",
        pipeline: "Pipeline",
        created: "Démarrée",
        job: "Identifiant",
        details: "Détail",
        open: "Ouvrir l'exécution {{id}}",
        empty: "Aucune exécution — lancez un outil ou le pipeline complet.",
        noMatch: "Aucune exécution ne correspond aux filtres actuels.",
        resetFilters: "Effacer les filtres",
        exportCsv: "Exporter en CSV",
        densityCompact: "Lignes compactes",
        densityComfortable: "Lignes confortables",
        densityLabel: "Densité des lignes",
        forkOf: "fork de {{id}}",
        filterYear: "Année",
        filterMonth: "Mois",
        filterDay: "Jour",
        anyYear: "Toute année",
        anyMonth: "Tout mois",
        anyDay: "Tout jour",
        dateFilterLabel: "Filtrer les exécutions par date de démarrage",
      },
      runDetail: {
        title: "Détail de l'exécution",
        back: "Toutes les exécutions",
        created: "Démarrée",
        finished: "Terminée",
        launchedBy: "Lancée par",
        forkedFrom: "Fork du run",
        forks_one: "{{count}} fork de ce run :",
        forks_other: "{{count}} forks de ce run :",
        steps: "Étapes",
        attempt: "tentative {{count}}",
        rows: "{{in}} → {{out}} lignes",
        errorSummary_one: "{{count}} contrôle/étape en échec demande votre attention",
        errorSummary_many: "{{count}} contrôles/étapes en échec demandent votre attention",
        errorSummary_other: "{{count}} contrôles/étapes en échec demandent votre attention",
        errorSummaryHint: "Chaque lien fait défiler jusqu'à l'étape concernée.",
        showError: "Afficher le détail de l'erreur",
        hideError: "Masquer le détail de l'erreur",
        askAudit: "Poser une question sur cette exécution",
        viewRun: "Voir l'exécution",
        awaiting: "Cette exécution est en pause à un point d'approbation.",
      },
      toasts: {
        jobCompleted: "Traitement terminé.",
        jobCompletedControls: "Traitement terminé — avec des contrôles à examiner.",
        jobFailed: "Le traitement a échoué.",
        approvalDecided: "Décision enregistrée.",
      },
      dashboard: {
        title: "Vue d'activité",
        subtitle: "Utilisation de la plateforme sur les {{days}} derniers jours",
        totalRuns: "Traitements",
        uploads: "Fichiers chargés",
        outputs: "Fichiers produits",
        completed: "Traitements terminés",
        activity: "Activité journalière",
        runs: "Traitements",
        uploadsSeries: "Chargements",
        toolUsage: "Utilisation des outils",
        recentJobs: "Derniers traitements",
        quickRunning: "En cours",
        quickWaiting: "En attente d'approbation",
        quickWarning: "Terminées avec contrôles",
        quickFailure: "Échecs",
        job: "Traitement",
        pipeline: "Pipeline",
        state: "État",
        date: "Date",
        empty: "Aucune activité — chargez un fichier et lancez un outil.",
        activityChart: "Histogramme des traitements et chargements journaliers",
        toolUsageChart: "Histogramme du nombre d'exécutions par outil",
        review: "Revues",
        reviewPending: "En attente de décision",
        reviewMedian: "Délai médian de décision",
        reviewMonth: "Décisions ce mois-ci",
        approved: "approuvées",
        rejected: "rejetées",
        replaced: "remplacées",
        health: "Santé des outils",
        healthTool: "Outil",
        healthRuns: "Exécutions",
        healthSuccess: "Réussies",
        healthControls: "Avec contrôles en échec",
        healthDuration: "Durée médiane",
      },
      monthly: {
        title: "Clôture, mois par mois",
        subtitleAll: "Cumul des {{count}} comptes du Summary, par activité — dernier run réussi de chaque mois.",
        subtitleAccount: "Compte {{account}}, par activité — dernier run réussi de chaque mois.",
        year: "Année de clôture",
        activity: "Activité",
        all: "Toutes",
        total: "Total",
        account: "Compte (tous les comptes si vide)",
        empty: "Aucun chiffre pour cette sélection — lancez le mapping des éléments exceptionnels avec une période de clôture.",
        chartLabel: "Histogramme du compte sélectionné par mois — {{series}}",
        calendar: "Calendrier de clôture {{year}} — état du dernier run de chaque mois. Cliquez un mois pour voir les comptes modifiés au cours du mois.",
        noRun: "Aucun run pour ce mois",
        uploads_one: "{{count}} chargement",
        uploads_many: "{{count}} chargements",
        uploads_other: "{{count}} chargements",
        noUpload: "Aucun chargement pour ce mois",
        clickHint: "Cliquez un mois pour voir les comptes modifiés",
        chartClickHint: "Cliquez une barre pour les chargements du mois et les comptes modifiés",
        changesTitle: "{{month}} — comptes modifiés au cours du mois",
        changesSubtitle: "{{uploads}} · {{compared}} avec chiffres · {{changed}} lignes de compte modifiées",
        changesLoading: "Comparaison des chargements du mois…",
        timeline: "Chargements du mois, dans l'ordre",
        uploadN: "Chargement {{n}}",
        by: "par {{user}}",
        noFile: "sans fichier d'entrée",
        notHarvested: "sans chiffres",
        openRun: "Ouvrir l'exécution",
        close: "Fermer",
        changedOnly: "Modifiés seulement",
        allAccounts: "Tous les comptes",
        searchAccount: "Compte ou libellé…",
        colAccount: "Compte",
        colLabel: "Libellé",
        colActivity: "Activité",
        colDelta: "Δ dernier − premier",
        absent: "absent",
        noUploads: "Aucun chargement n'a été lancé pour ce mois.",
        noFigures: "Aucun chargement de ce mois n'a encore produit de chiffres — les comptes ne peuvent pas être comparés.",
        needsNextTool: "Le chargement {{n}} a exécuté {{tool}}, qui ne produit pas de chiffres à lui seul : ils apparaîtront une fois {{next}} exécuté sur son résultat.",
        runNextTool: "Exécuter {{tool}} dessus",
        figuresSource: "Chiffres issus de {{tool}}",
        figuresAtLevel: "Ces chiffres viennent de {{tool}}. Exécutez {{next}} sur le résultat du dernier chargement pour obtenir les chiffres mappés ; ils prendront le relais sur ce mois.",
        launching: "Lancement…",
        nextToolStarted: "{{tool}} lancé sur le fichier produit — ses chiffres apparaîtront ici une fois terminé.",
        oneUpload: "Un seul chargement a produit des chiffres ce mois-ci — rien à comparer pour l'instant ; ses chiffres sont affichés.",
        noChange: "Aucun compte n'a changé entre les chargements de ce mois.",
        noMatch: "Aucun compte ne correspond à la recherche.",
      },
      wizard: {
        stepCount_one: "{{count}} étape, validée avant la suivante",
        stepCount_many: "{{count}} étapes, chacune validée avant la suivante",
        stepCount_other: "{{count}} étapes, chacune validée avant la suivante",
        title: "Pipeline complet",
        subtitle: "Le pipeline exécute chaque étape déclarée dans l'ordre, avec arrêt à chaque point d'approbation.",
        stepUpload: "Charger le fichier d'entrée",
        stepIdentity: "Identité du traitement",
        stepRun: "Lancer et suivre",
        identityHelp: "La dernière étape du pipeline a besoin de ces valeurs — elles estampillent chaque ligne produite.",
        identityUnavailable: "Les champs d'identité n'ont pas pu être chargés : le pipeline ne peut pas être lancé en sécurité.",
        pickDag: "Choisissez un pipeline",
        changeDag: "Choisir un autre pipeline",
        launch: "Démarrer le pipeline",
        launching: "Démarrage…",
        next: "Suivant",
        back: "Retour",
        follow: "Avancement du pipeline",
        awaiting: "Cette étape attend votre approbation.",
        review: "Examiner et décider",
        files: "Fichiers produits",
        newRun: "Lancer un autre traitement",
        noDag: "Aucun pipeline n'est déclaré pour l'instant.",
        uploadFirst: "Chargez un fichier pour continuer.",
        noFileNeeded: "Aucun fichier n'est nécessaire pour la source de données choisie.",
        entryParams: "Source des données",
        forkFrom: "Départ depuis un fichier existant : {{name}}. Le run sera enregistré comme fork du run qui l'a produit. Déposez un autre fichier pour le remplacer.",
        fillIdentity: "Tous les champs d'identité sont requis pour un pipeline.",
      },
      params: {
        unknownControl: "Type de champ inconnu : {{control}}",
      },
      tools: {
        title: "Outil unique",
        subtitle: "Choisissez un outil, chargez son fichier d'entrée, lancez, téléchargez le résultat.",
        pick: "1 · Choisir un outil",
        upload: "2 · Source des données",
        run: "3 · Lancer",
        progress: "4 · Avancement",
        files: "5 · Fichiers",
        requiredColumns: "Colonnes attendues",
        checkingFile: "Vérification du fichier chargé pour cet outil…",
        fileOk: "Le fichier chargé correspond à l'entrée de cet outil.",
        fileMismatch: "Ce fichier ne correspond pas à cet outil — l'exécution échouerait :",
        fileMismatchHint: "Choisissez l'outil correspondant au fichier, ou chargez le bon fichier.",
        runButton: "Lancer {{name}}",
        running: "Exécution…",
        missing: "Champ(s) requis manquant(s) : {{fields}}",
        overrideReason: "Motif de dérogation (requis en cas de dérogation)",
        advanced: "Paramètres avancés",
        uploadPrompt: "Déposez un fichier .xlsx ici ou cliquez pour parcourir",
        uploading: "Chargement…",
        hitl: "Approbation",
        version: "version",
        empty: "Aucun outil n'est déclaré pour l'instant.",
        multiSlot: "Cet outil déclare plusieurs fichiers d'entrée — cette version du tableau de bord ne prend en charge que le premier.",
        noFileNeeded: "Aucun fichier n'est nécessaire pour la source de données choisie.",
      },
      audit: {
        title: "Assistance audit",
        subtitle: "Interrogez les traitements, les chiffres, les règles — ou joignez un classeur et posez vos questions.",
        placeholder: "ex. Quel est le total du compte 5311100020 par année ?",
        send: "Envoyer",
        thinking: "Analyse…",
        attach: "Joindre un classeur",
        attached: "Joint : {{name}}",
        removeAttachment: "Retirer la pièce jointe",
        sqlUsed: "SQL exécuté",
        sources: "Sources",
        intent: {
          qualitative: "règles",
          quantitative: "données",
          hybrid: "règles + données",
          file: "fichier joint",
        },
        gathered: {
          run: "run en contexte",
          attachment: "fichier lu",
          documents: "documents de règles",
          query: "requête base",
        },
        readingNotes: "Ce que l'assistant a lu dans le fichier joint",
        empty: "Posez une première question — l'assistant lit la base des traitements et vos documents de règles.",
        context: "Contexte : exécution {{id}}",
        contextRemove: "Retirer le contexte d'exécution",
        clear: "Effacer la conversation",
        copyAnswer: "Copier la réponse",
        copySql: "Copier le SQL",
      },
      auditActions: {
        heading: "Actions proposées (rien ne s'exécute sans votre confirmation)",
        fixParams: "Corriger les paramètres et relancer",
        fixFile: "Corriger le fichier et relancer",
        reviewGuardrails: "Examiner les garde-fous en échec",
        rerunSameInput: "Relancer avec le même fichier d'entrée",
        openApprovals: "Ouvrir les approbations en attente",
        reviewControls: "Examiner les contrôles en échec",
        retryJob: "Relancer cette exécution",
        relaunchFromTool: "Relancer depuis la page outil",
        confirmTitle: "Confirmer la relance",
        confirmBody:
          "Une nouvelle exécution de {{tool}} sera lancée sur le fichier {{file}}…. Rien d'autre n'est modifié. Confirmer ?",
        confirmRetryBody:
          "L'exécution {{id}}… sera relancée comme NOUVELLE exécution, en rejouant le fichier d'entrée et les paramètres enregistrés. L'exécution d'origine est conservée telle quelle. Confirmer ?",
        confirm: "Confirmer et relancer",
        launching: "Lancement…",
        rerunStarted: "Nouvelle exécution lancée.",
      },
      files: {
        download: "Télécharger",
        rows_one: "{{count}} ligne",
        rows_many: "{{count}} lignes",
        rows_other: "{{count}} lignes",
        noRows: "classeur",
        produced: "Fichiers produits",
        lineage: "Généalogie",
        lineageOriginal: "Chargement d'origine — aucun artefact parent.",
        fork: "Lancer un outil à partir de ce fichier (fork)",
        fromRun: "issu du run {{id}}",
        inRun: "run {{id}}",
        usedBy: "Utilisé par :",
        relationDerived: "produit à partir de",
        relationReplaced: "remplacé par un chargement",
        relationRevised: "révisé avec l'assistant",
        unmatchedShort_one: "{{count}} ligne non rapprochée",
        unmatchedShort_many: "{{count}} lignes non rapprochées",
        unmatchedShort_other: "{{count}} lignes non rapprochées",
      },
      upload: {
        constraint: ".xlsx · jusqu'à {{mb}} Mo",
        badExtension: "{{name}} — seuls les fichiers .xlsx / .xlsm sont acceptés.",
        tooLarge: "{{name}} : {{size}} Mo dépasse la limite de {{max}} Mo.",
        nameHint: "se terminant par _{{code}}.xlsx",
        badFormat: "{{name}} doit se terminer par _{{code}}.xlsx, le code du client sur lequel vous travaillez.",
        customerMismatch: "{{name}} porte le code société « {{found}} » mais vous travaillez sur {{customer}} ({{code}}). Renommez le fichier ou changez de client.",
      },
      progress: {
        state: "État",
        step: "Étape",
        rowsOut: "Lignes",
        error: "Détail de l'erreur",
      },
      approval: {
        title: "Approbation — {{step}}",
        approve: "Approuver",
        reject: "Rejeter",
        replace: "Remplacer le fichier et approuver",
        note: "Note",
        decided: "Décision enregistrée.",
        chooseFile: "Choisissez le classeur corrigé à charger.",
        chosenFile: "Sélectionné : {{name}}",
        output: "résultat",
        askAgent: "Demander à l'agent",
        askAgentAgain: "Redemander à l'agent",
        agentBusy: "L'agent examine le document…",
        agentHeading: "Avis de l'agent",
        agentQuestion:
          "Examine le document produit par l'étape {{step}} avant son approbation : avertissements de l'outil, contrôles en échec, lignes non appariées, anomalies. Indique ce qui bloque l'approbation, ce qui est seulement à vérifier, et ce qu'il faut corriger avant d'approuver.",
        unmatchedWarning_one: "{{count}} ligne n'a été couverte par aucune règle explicite et a reçu une valeur par défaut. Vérifiez-la avant d'approuver.",
        unmatchedWarning_many: "{{count}} lignes n'ont été couvertes par aucune règle explicite et ont reçu une valeur par défaut. Vérifiez-les avant d'approuver.",
        unmatchedWarning_other: "{{count}} lignes n'ont été couvertes par aucune règle explicite et ont reçu une valeur par défaut. Vérifiez-les avant d'approuver.",
      },
      states: {
        COMPLETED: "Terminé",
        RUNNING: "En cours",
        FAILED: "Échec",
        VALIDATION_FAILED: "Validation échouée",
        AWAITING_HUMAN_APPROVAL: "En attente d'approbation",
        INITIALIZED: "Initialisé",
        SUCCEEDED: "Réussi",
        SUCCESS: "Réussi",
        PENDING: "En attente",
        QUEUED: "En file",
        SUBMITTED: "Soumis",
        CLAIMED: "Pris en charge",
        STARTED: "Démarré",
        PAUSED: "En pause",
        AWAITING_APPROVAL: "En attente d'approbation",
        REJECTED: "Rejeté",
        SKIPPED: "Ignoré",
        CANCELLED: "Annulé",
        COMPLETED_WITH_CONTROLS: "Terminé avec contrôles",
        SUCCESS_WITH_CONTROLS: "Terminé avec contrôles",
      },
      search: {
        tools: "Outils",
        jobs: "Traitements",
        files: "Fichiers",
        noResults: "Aucun résultat pour « {{q}} »",
      },
      controls: {
        title: "Contrôles de réconciliation",
        inputFile: "Fichier d'entrée traité",
        comparedTo: "Par rapport à l'exécution précédente : {{before}} contrôle(s) en échec avant, {{after}} maintenant.",
        previously: "Exécution précédente",
        delta: {
          cleared: "résolu",
          detailChanged: "modifié — votre correction a été traitée",
          unchanged: "inchangé",
          regressed: "dégradé",
        },
        ok: "OK",
        failed: "ÉCHEC",
        downloadPdf: "Télécharger le rapport (PDF)",
        downloadingPdf: "Préparation…",
        downloadPdfHint:
          "Tous les contrôles en langage clair — y compris ce que l'ancien classeur ne signalait pas.",
        downloadPdfFailed: "Le rapport n'a pas pu être produit.",
        flaggedLines_one: "{{count}} ligne à examiner",
        flaggedLines_many: "{{count}} lignes à examiner",
        flaggedLines_other: "{{count}} lignes à examiner",
        fixTitle: "Corriger et retraiter",
        fixHint: "Décrivez une correction du fichier d'entrée — l'assistant la traduit en modifications précises que vous validez avant toute application.",
        fixHintFailed: "Des contrôles ont échoué. Décrivez la correction avec vos mots — l'assistant la traduit en modifications précises du fichier d'entrée, que vous validez avant toute application.",
        instructionPlaceholder: "ex. Supprimer les lignes M540000102 et M540000000 de la feuille CEM — ce sont des sous-totaux hors référentiel.",
        propose: "Proposer une correction",
        proposing: "Analyse…",
        emptyPlan: "L'assistant n'a pas pu traduire cette instruction en modifications du fichier.",
        opAction: "Action",
        opTarget: "Cible",
        opReason: "Motif",
        op: {
          set_cell: "Modifier la cellule",
          delete_rows: "Supprimer ligne(s)",
          replace_value: "Remplacer la valeur",
        },
        apply: "Appliquer et créer le fichier corrigé",
        applying: "Application…",
        appliedMessage: "{{name}} créé — {{count}} modification(s). Vérifiez-le, puis retraitez.",
        rerun: "Retraiter",
        rerunning: "Démarrage…",
      },
      validation: {
        statuses: {
          ok: "OK",
          moved: "Déplacée",
          renamed: "Renommée",
          missing: "Manquante",
          extra: "Inattendue",
        },
        title: "Rapport de validation",
        errors_one: "{{count}} erreur",
        errors_many: "{{count}} erreurs",
        errors_other: "{{count}} erreurs",
        row: "Ligne",
        column: "Colonne",
        code: "Code",
        value: "Valeur",
        detail: "Détail",
        driftTitle: "Dérive de gabarit détectée",
        driftBody: "Le classeur chargé ne correspond pas au gabarit déclaré. Rien n'a été calculé.",
        expected: "Attendu",
        index: "Position",
        found: "Trouvé",
        status: "Statut",
        columnStat: "{{column}} : {{invalid}} invalide(s), {{empty}} vide(s)",
      },
    },
  },
};

function readStoredLanguage(): string | null {
  try {
    return typeof window !== "undefined"
      ? window.localStorage.getItem(LS_LANG_KEY)
      : null;
  } catch {
    return null;
  }
}

const stored = readStoredLanguage();
const browser =
  typeof navigator !== "undefined" && navigator.language.toLowerCase().startsWith("fr")
    ? "fr"
    : "en";

void i18n.use(initReactI18next).init({
  resources,
  lng: stored ?? browser,
  fallbackLng: "en",
  interpolation: { escapeValue: false },
});

/** Keep <html lang> and the tab title aligned with the active language. */
function syncDocument(lng: string) {
  if (typeof document === "undefined") return;
  document.documentElement.lang = lng.startsWith("fr") ? "fr" : "en";
  document.title = i18n.t("app.title");
}

syncDocument(i18n.language);
i18n.on("languageChanged", syncDocument);

export function setLanguage(lang: "en" | "fr") {
  void i18n.changeLanguage(lang);
  try {
    window.localStorage.setItem(LS_LANG_KEY, lang);
  } catch {
    /* private mode */
  }
}

export default i18n;
